import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import SocialButtons from '../components/SocialButtons';
import { PRIMARY } from '../components/CaptureSheets';
import { kcal } from '../lib/format';
import { useApp, useAppPath } from '../lib/AppContext';
import AvoidFoods from './AvoidFoods';

const CARD = 'flex flex-col gap-3 rounded-[20px] bg-base-100 p-4';
const STARTS = [{ id: 'tonight', label: 'Tối nay' }, { id: 'tomorrow', label: 'Ngày mai' }, { id: 'next_week', label: 'Tuần sau' }];

function Shell({ children }) {
  return (
    <div data-theme="macro" className="min-h-dvh bg-base-200 text-base-content">
      <main className="pt-safe pb-safe mx-auto flex max-w-md flex-col gap-4 px-4 py-6">{children}</main>
    </div>
  );
}

function Choice({ checked, title, text, onClick }) {
  return (
    <button type="button" aria-pressed={checked} onClick={onClick}
      className={`flex items-start gap-3 rounded-[16px] border p-3 text-left ${checked ? 'border-[#17804f] bg-[#eef7f1]' : 'border-line bg-base-100'}`}>
      <span className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border-2 ${checked ? 'border-[#17804f]' : 'border-line'}`}>
        {checked && <span className="size-2.5 rounded-full bg-[#17804f]" />}
      </span>
      <span><span className="block font-semibold">{title}</span>{text && <span className="block text-xs text-muted">{text}</span>}</span>
    </button>
  );
}

// ③ The page a cooking invite opens (/n/<token>): who invites, your own targets (the inviter never sees them),
// the foods you can't eat, when to start and, if you already have a menu, where the shared dishes come from.
export default function AcceptPage() {
  const { api, signedIn, membership } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [providers, setProviders] = useState(null);
  const [foods, setFoods] = useState([]);
  const [starts, setStarts] = useState('tonight');
  const [source, setSource] = useState('inviter');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [inPair, setInPair] = useState(null);

  useEffect(() => {
    api.get(`/pair_invites/by_token/${token}`).then((d) => {
      setInfo(d);
      setFoods(d.avoid_foods || []);
      if (d.suggested_start) setStarts(d.suggested_start);
    }).catch((e) => setError(e.status === 404 ? 'not_found' : e.message));
    if (!signedIn) api.get('/auth/providers').then(setProviders).catch(() => setProviders({}));
  }, [api, token, signedIn]);

  const accept = async (leaveCurrent = false) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/pair_invites/by_token/${token}/accept`, { avoid_foods: foods, starts, source, leave_current: leaveCurrent || undefined });
      navigate(path('/plan'), { replace: true });
    } catch (e) {
      if (e.code === 'in_pair') setInPair(e.data?.partner_name || info?.current_partner?.name);
      else if (e.code === 'gone') setInfo((i) => ({ ...i, status: 'expired' }));
      else setError(e.message);
      setBusy(false);
    }
  };

  if (error === 'not_found' || (info && info.status !== 'pending')) {
    return (
      <Shell>
        <section className="flex flex-col items-center gap-3 rounded-[24px] bg-base-100 p-6 text-center">
          <Icon name="clock" className="size-10 text-muted" />
          <p className="text-lg font-bold">Lời mời không còn dùng được</p>
          <p className="text-sm text-muted">Lời mời đã hết hạn, đã được dùng hoặc đã bị huỷ. Nhờ người mời gửi lại link mới nhé.</p>
          {signedIn ? <Link to={path('/')} className="btn h-11 rounded-2xl">Về trang chủ</Link> : <a href="/login" className="btn h-11 rounded-2xl">Đăng nhập</a>}
        </section>
      </Shell>
    );
  }
  if (!info) return <Shell>{error ? <p role="alert" className="text-sm text-error">{error}</p> : <div className="skeleton h-64 rounded-[24px]" />}</Shell>;

  const inviter = info.inviter;
  const header = (
    <section className="flex flex-col gap-2 rounded-[20px] bg-[#fdf0e6] p-4">
      <div className="flex items-center gap-3">
        <span className="flex -space-x-3">
          <Avatar name={inviter.name} url={inviter.avatar_url} size={44} />
          <span className="grid size-11 place-items-center rounded-full border-2 border-[#fdf0e6] bg-[#f6d2dc] font-bold text-[#8e2949]">
            {signedIn ? '·' : '+'}
          </span>
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[#c2410c]">Lời mời nấu chung</p>
          <p className="truncate text-xl font-extrabold">{inviter.name} mời bạn</p>
        </div>
      </div>
      <p className="text-sm text-[#5c3a1e]">
        Ăn chung thực đơn{info.week ? ` tuần ${info.week}` : ''}. App tự chia khẩu phần theo mục tiêu của bạn.
      </p>
    </section>
  );

  if (!signedIn) {
    return (
      <Shell>
        {header}
        <section className="flex flex-col gap-3 rounded-[24px] bg-base-100 p-5">
          <p className="text-center font-bold">Đăng nhập hoặc tạo tài khoản để nấu chung</p>
          {providers && <SocialButtons providers={providers} onDone={() => { window.location.href = `/me/n/${token}`; }} />}
          <a href="/login" className="text-center text-sm font-medium text-muted underline">Đăng nhập bằng email / số điện thoại</a>
        </section>
      </Shell>
    );
  }

  const targets = info.my_targets || {};
  const pro = info.viewer_pro ?? membership?.pro;
  return (
    <Shell>
      {header}
      {info.own && <p className="rounded-[20px] bg-[#fdf3d7] p-4 text-sm">Đây là lời mời bạn tạo, hãy gửi cho người nấu chung.</p>}
      <section className={CARD}>
        <div className="flex items-center justify-between">
          <p className="font-bold">Mục tiêu của bạn</p>
          <Link to={path('/settings')} className="text-sm font-semibold text-brand-text">Sửa</Link>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-[14px] bg-base-200 px-3 py-2">
            <p className="tabular text-xl font-extrabold">{targets.calories ? kcal(targets.calories) : '—'}</p>
            <p className="text-xs text-muted">kcal / ngày</p>
          </div>
          <div className="rounded-[14px] bg-[#e6eefc] px-3 py-2 text-[#2456b0]">
            <p className="tabular text-xl font-extrabold">{targets.protein_g ? `${targets.protein_g} g` : '—'}</p>
            <p className="text-xs">đạm / ngày</p>
          </div>
        </div>
        <p className="text-xs text-muted">
          {targets.calories ? `Lấy từ hồ sơ của bạn. ${inviter.name} không thấy con số này.` : 'Bạn chưa đặt mục tiêu: app dùng mức mặc định, hãy đặt trong Cài đặt để khẩu phần sát hơn.'}
        </p>
      </section>
      <section className={CARD}>
        <p className="font-bold">Bạn không ăn được</p>
        <AvoidFoods value={foods} onChange={setFoods} />
        <p className="text-xs text-muted">Món có những thứ này sẽ được đổi ở các bữa ăn chung. {inviter.name} cũng thấy danh sách này để nấu đúng.</p>
      </section>
      <section className={CARD}>
        <p className="font-bold">Bắt đầu từ</p>
        <div className="grid grid-cols-3 gap-2">
          {STARTS.map((s) => (
            <button key={s.id} type="button" aria-pressed={starts === s.id} onClick={() => setStarts(s.id)}
              className={`h-11 rounded-[14px] border font-semibold ${starts === s.id ? 'border-neutral bg-neutral text-neutral-content' : 'border-line bg-base-100'}`}>
              {s.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted">Ngày đã qua và bữa đã ghi giữ nguyên.</p>
      </section>
      {info.my_menu && (
        <section className={CARD}>
          <p className="font-bold">Món cho bữa ăn chung</p>
          <Choice checked={source === 'inviter'} onClick={() => setSource('inviter')} title={`Dùng thực đơn của ${inviter.name}`} text="Mặc định, không tốn lượt AI" />
          <Choice checked={source === 'mine'} onClick={() => setSource('mine')} title="Dùng thực đơn của tôi" text={`${inviter.name} sẽ được báo`} />
          <Choice checked={source === 'ai'} onClick={() => setSource('ai')} title="Để AI chọn món hợp cả hai"
            text={pro ? 'Dùng lượt AI tạo thực đơn của bạn' : 'Cần gói Pro'} />
          <p className="text-xs text-muted">Bữa riêng của cả hai giữ nguyên.</p>
        </section>
      )}
      {inPair && (
        <section className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
          <p className="text-sm">Rời nhóm với {inPair} để nấu chung với {inviter.name}? {inPair} sẽ được báo và giữ nguyên thực đơn.</p>
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => accept(true)}>
            {busy && <span className="loading loading-spinner loading-sm" />} Rời nhóm và nấu chung với {inviter.name}
          </button>
        </section>
      )}
      {error && <p role="alert" className="text-center text-sm font-medium text-error">{error}</p>}
      {!info.own && !inPair && (
        <div className="flex flex-col gap-2 pt-2">
          <button type="button" className={PRIMARY} disabled={busy || (source === 'ai' && !pro)} onClick={() => accept()}>
            {busy && <span className="loading loading-spinner loading-sm" />} Nấu chung với {inviter.name}
          </button>
          <Link to={path('/')} className="btn btn-ghost h-11 rounded-2xl">Để sau</Link>
        </div>
      )}
    </Shell>
  );
}
